import { type FC, useState } from "react";
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Checkbox,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useWidgetsQuery, type WidgetsItem } from "@/partner/entities/widgets";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { interpolationFormat } from "@/partner/shared/lib";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { WidgetsListToolbar } from "./WidgetsListToolbar";

interface WidgetsListProps {
  onClickRow: (widget: WidgetsItem) => void;
}

function getButtonStyle(item: WidgetsItem) {
  return {
    backgroundColor: item.buttonColor,
    color: item.buttonTextColor,
  };
}

export const WidgetsList: FC<WidgetsListProps> = ({ onClickRow }) => {
  const { t, i18n } = useTranslation();
  // Референсный WidgetList.js прячет preview/hits/clicks/ctr/created на
  // мобильном, оставляя только чекбокс+название — та же граница, что уже
  // использует ListPageHeader этой же страницы.
  const isMobile = useDeviceBreakpoint("md");
  // Страница и размер живут в URL (FilterProvider), как у остальных списков.
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const { data, loading } = useWidgetsQuery({
    variables: { page, rowsPerPage },
    fetchPolicy: "cache-and-network",
  });

  const nodes = data?.widgets.nodes ?? [];
  const count = data?.widgets.count ?? 0;

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < nodes.length;

  const onSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(
        nodes.reduce((all, item) => ({ ...all, [item.id]: true }), {})
      );
    } else {
      setSelected({});
    }
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (nodes.length === 0) {
    return <EmptyListCard title={t("widgets.list.empty")} />;
  }

  return (
    <Card elevation={0}>
      {numSelected > 0 && (
        <WidgetsListToolbar
          indeterminate={indeterminate}
          numSelected={numSelected}
          nodes={nodes}
          onSelectAllClick={onSelectAllClick}
          selectedItems={selectedIds.map((id) => ({ id }))}
          onCloseSelected={() => setSelected({})}
        />
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="default"
                indeterminate={indeterminate}
                checked={numSelected === nodes.length}
                onChange={onSelectAllClick}
              />
            </TableCell>
            <TableCell>{t("widgets.list.titleColumn")}</TableCell>
            {!isMobile && (
              <TableCell>{t("widgets.list.previewColumn")}</TableCell>
            )}
            {!isMobile && <TableCell>{t("widgets.list.hits")}</TableCell>}
            {!isMobile && <TableCell>{t("widgets.list.clicks")}</TableCell>}
            {!isMobile && <TableCell>{t("widgets.list.ctr")}</TableCell>}
            {!isMobile && (
              <TableCell align="right">{t("widgets.list.created")}</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.map((item) => (
            <TableRow
              key={item.id}
              hover
              selected={Boolean(selected[item.id])}
              onClick={() => onClickRow(item)}
              sx={{ cursor: "pointer" }}
            >
              <TableCell
                padding="checkbox"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  color="default"
                  checked={Boolean(selected[item.id])}
                  onChange={(event) =>
                    setSelected({
                      ...selected,
                      [item.id]: event.target.checked,
                    })
                  }
                />
              </TableCell>
              <TableCell>
                <ItemTitleLink
                  to={`${PagePaths.widgets}?tab=edit&id=${item.id}`}
                  onOpen={() => onClickRow(item)}
                  color="text.primary"
                  underline="none"
                >
                  {item.title}
                </ItemTitleLink>
              </TableCell>
              {!isMobile && (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button
                    disableElevation
                    href={item.buttonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={getButtonStyle(item)}
                    variant="contained"
                  >
                    {item.buttonText}
                  </Button>
                </TableCell>
              )}
              {!isMobile && <TableCell>{item.hits}</TableCell>}
              {!isMobile && <TableCell>{item.clicks}</TableCell>}
              {!isMobile && <TableCell>{item.ctr.toFixed(1)}%</TableCell>}
              {!isMobile && (
                <TableCell align="right">
                  {interpolationFormat(item.createdAt, "short", i18n.language)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
